import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';

const FIELDS    = ['eventType','tokenBought','tokenSold','amountBought','amountSold','walletAddress'];
const OPERATORS = ['equals','notEquals','greaterThan','lessThan','contains','exists'];
const ACTIONS   = ['telegram_alert','log'];

const emptyCondition = () => ({ field: 'eventType', operator: 'equals', value: 'swap' });
const emptyAction    = () => ({ type: 'telegram_alert', message: '🔔 {{walletAddress}} — {{eventType}} detected! Token: {{tokenBought}}' });

export const RulesPage = ({ toast }) => {
  const [rules,   setRules]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [showing, setShowing] = useState(false);

  // Form state
  const [name,       setName]       = useState('');
  const [desc,       setDesc]       = useState('');
  const [walletAddr, setWalletAddr] = useState('');
  const [conditions, setConditions] = useState([emptyCondition()]);
  const [actions,    setActions]    = useState([emptyAction()]);
  const [submitting, setSub]        = useState(false);

  const loadRules = useCallback(async () => {
    try {
      const r = await api.getRules();
      setRules(r.data || []);
    } catch (e) { toast(e.message, 'error'); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { loadRules(); }, [loadRules]);

  const resetForm = () => {
    setName(''); setDesc(''); setWalletAddr('');
    setConditions([emptyCondition()]); setActions([emptyAction()]);
    setShowing(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSub(true);
    try {
      await api.createRule({
        name, description: desc,
        walletAddress: walletAddr.trim() || null,
        conditions, actions,
      });
      toast('Rule created!');
      resetForm();
      loadRules();
    } catch (er) { toast(er.message, 'error'); }
    finally { setSub(false); }
  };

  const handleDelete = async (id, ruleName) => {
    if (!window.confirm(`Delete rule "${ruleName}"?`)) return;
    try { await api.deleteRule(id); toast('Rule deleted'); loadRules(); }
    catch (e) { toast(e.message, 'error'); }
  };

  const handleTest = async (id) => {
    try { await api.testRule(id); toast('Test fired — check Telegram / logs'); }
    catch (e) { toast(e.message, 'error'); }
  };

  const handleToggle = async (rule) => {
    try {
      await api.updateRule(rule._id, { isActive: !rule.isActive });
      loadRules();
    } catch (e) { toast(e.message, 'error'); }
  };

  // Condition helpers
  const updateCond = (i, field, val) =>
    setConditions(p => p.map((c, idx) => idx === i ? { ...c, [field]: val } : c));
  const addCond    = () => setConditions(p => [...p, emptyCondition()]);
  const removeCond = (i) => setConditions(p => p.filter((_, idx) => idx !== i));

  // Action helpers
  const updateAct  = (i, field, val) =>
    setActions(p => p.map((a, idx) => idx === i ? { ...a, [field]: val } : a));

  return (
    <div style={S.page}>
      {/* Create rule */}
      <div style={S.card}>
        <div style={S.cardHeader}>
          <h2 style={S.cardTitle}>Automation Rules</h2>
          <button style={S.btnGreen} onClick={() => setShowing(!showing)}>
            {showing ? '✕ Cancel' : '+ New Rule'}
          </button>
        </div>

        {showing && (
          <form onSubmit={handleSubmit} style={S.form}>
            <div style={S.row2}>
              <div style={S.field}>
                <label>Rule Name *</label>
                <input value={name} onChange={e => setName(e.target.value)}
                  placeholder="e.g. Alert on any swap" required maxLength={100} />
              </div>
              <div style={S.field}>
                <label>Scope to Wallet (optional)</label>
                <input value={walletAddr} onChange={e => setWalletAddr(e.target.value)}
                  placeholder="Leave blank to apply to all wallets" />
              </div>
            </div>

            <div style={S.field}>
              <label>Description (optional)</label>
              <input value={desc} onChange={e => setDesc(e.target.value)}
                placeholder="What does this rule do?" maxLength={300} />
            </div>

            {/* Conditions */}
            <div style={S.section}>
              <div style={S.sectionHeader}>
                <span style={S.sectionLabel}>IF — Conditions (ALL must match)</span>
                <button type="button" style={S.btnSm} onClick={addCond}>+ Add</button>
              </div>
              {conditions.map((c, i) => (
                <div key={i} style={S.condRow}>
                  <select value={c.field} onChange={e => updateCond(i, 'field', e.target.value)} style={S.sel}>
                    {FIELDS.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                  <select value={c.operator} onChange={e => updateCond(i, 'operator', e.target.value)} style={S.sel}>
                    {OPERATORS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                  {c.operator !== 'exists' && (
                    <input value={c.value} onChange={e => updateCond(i, 'value', e.target.value)}
                      placeholder="value" style={{ flex: 1 }} />
                  )}
                  {conditions.length > 1 &&
                    <button type="button" style={S.btnX} onClick={() => removeCond(i)}>✕</button>}
                </div>
              ))}
            </div>

            {/* Actions */}
            <div style={S.section}>
              <div style={S.sectionHeader}>
                <span style={S.sectionLabel}>THEN — Actions</span>
              </div>
              {actions.map((a, i) => (
                <div key={i} style={S.actionBlock}>
                  <div style={S.field}>
                    <label>Action Type</label>
                    <select value={a.type} onChange={e => updateAct(i, 'type', e.target.value)}>
                      {ACTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  {a.type === 'telegram_alert' && (
                    <div style={S.field}>
                      <label>Message Template</label>
                      <textarea value={a.message} rows={3}
                        onChange={e => updateAct(i, 'message', e.target.value)}
                        placeholder="Use {{walletAddress}}, {{eventType}}, {{tokenBought}} etc." />
                      <span style={S.hint}>
                        Available: {'{{walletAddress}} {{eventType}} {{tokenBought}} {{tokenSold}} {{amountBought}} {{amountSold}} {{signature}}'}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <button type="submit" style={S.btnGreen} disabled={submitting}>
              {submitting ? '⏳ Creating…' : '✓ Create Rule'}
            </button>
          </form>
        )}
      </div>

      {/* Rules list */}
      <div style={S.card}>
        <h2 style={S.cardTitle}>Active Rules <span style={S.badge}>{rules.length}</span></h2>
        {loading ? <p style={S.dim}>Loading…</p> :
         rules.length === 0 ? <p style={S.dim}>No rules yet. Create your first one above.</p> :
         rules.map(rule => (
          <div key={rule._id} style={S.ruleRow}>
            <div style={S.ruleLeft}>
              <span style={S.dot(rule.isActive)} />
              <div>
                <div style={S.ruleName}>{rule.name}</div>
                {rule.description && <div style={S.ruleDesc}>{rule.description}</div>}
                <div style={S.ruleMeta}>
                  {rule.conditions.length} condition{rule.conditions.length !== 1 && 's'} →{' '}
                  {rule.actions.length} action{rule.actions.length !== 1 && 's'} ·{' '}
                  Triggered {rule.triggerCount}×
                  {rule.walletAddress &&
                    <> · Wallet: <code style={S.mono}>{rule.walletAddress.slice(0,8)}…</code></>}
                </div>
              </div>
            </div>
            <div style={S.ruleActions}>
              <button style={S.btnSm} onClick={() => handleTest(rule._id)}>Test</button>
              <button style={{ ...S.btnSm, color: rule.isActive ? '#ffd166' : '#00ff88' }}
                onClick={() => handleToggle(rule)}>
                {rule.isActive ? 'Pause' : 'Resume'}
              </button>
              <button style={{ ...S.btnSm, color: '#ff4d6a' }} onClick={() => handleDelete(rule._id, rule.name)}>
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const S = {
  page:        { display:'flex', flexDirection:'column', gap:24 },
  card:        { background:'#0d1117', border:'1px solid #1e2d3d', borderRadius:12, padding:'24px 28px' },
  cardHeader:  { display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 },
  cardTitle:   { fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:18, display:'flex', alignItems:'center', gap:10 },
  badge:       { background:'#00ff8820', color:'#00ff88', border:'1px solid #00ff8840',
                 borderRadius:20, fontSize:11, padding:'2px 10px', fontFamily:"'Space Mono',monospace" },
  form:        { display:'flex', flexDirection:'column', gap:18 },
  row2:        { display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 },
  field:       { display:'flex', flexDirection:'column', gap:6 },
  section:     { background:'#080b10', border:'1px solid #1e2d3d', borderRadius:8, padding:16, display:'flex', flexDirection:'column', gap:10 },
  sectionHeader:{ display:'flex', alignItems:'center', justifyContent:'space-between' },
  sectionLabel:{ fontSize:11, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', color:'#7a94b0' },
  condRow:     { display:'flex', gap:10, alignItems:'center' },
  sel:         { flex:'0 0 160px' },
  actionBlock: { background:'#0d1117', border:'1px solid #243447', borderRadius:8, padding:14, display:'flex', flexDirection:'column', gap:12 },
  hint:        { fontSize:11, color:'#7a94b0', fontFamily:"'Space Mono',monospace", marginTop:4 },
  btnGreen:    { background:'#00ff88', color:'#080b10', padding:'10px 22px', fontWeight:700, fontSize:13, borderRadius:8, alignSelf:'flex-start' },
  btnSm:       { background:'#131920', color:'#e0eaf5', border:'1px solid #1e2d3d', padding:'6px 14px', fontSize:12, borderRadius:6 },
  btnX:        { background:'transparent', color:'#ff4d6a', border:'none', fontSize:16, padding:'0 4px' },
  dim:         { color:'#7a94b0', fontStyle:'italic', padding:'8px 0' },
  ruleRow:     { display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12,
                 padding:'14px 0', borderBottom:'1px solid #1e2d3d' },
  ruleLeft:    { display:'flex', alignItems:'flex-start', gap:12, flex:1, minWidth:0 },
  ruleName:    { fontWeight:700, fontSize:15, marginBottom:2 },
  ruleDesc:    { fontSize:12, color:'#7a94b0', marginBottom:4 },
  ruleMeta:    { fontSize:11, color:'#7a94b0', fontFamily:"'Space Mono',monospace" },
  ruleActions: { display:'flex', gap:8, flexShrink:0, alignItems:'center' },
  dot:         (a) => ({ width:8, height:8, borderRadius:'50%', flexShrink:0, marginTop:6,
                 background: a ? '#00ff88' : '#7a94b0', boxShadow: a ? '0 0 6px #00ff88' : 'none' }),
  mono:        { fontFamily:"'Space Mono',monospace", fontSize:11 },
};
